import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  buildTicketType,
  buildTypeFormQuery,
  type BuildPhase,
  type BuildPiece,
} from '../../api/settingsEaseedeskTicketTypes'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → un tip de tichet, facut uitandu-te la el.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_types/build/index.php`.
 *
 * Se scriu fazele prin care trece tichetul si ce se cere pe fiecare. Fluxul,
 * statusurile, pasii si formularele se scriu singure - aceleasi randuri pe care
 * le-ar fi scris omul prin cele patru ecrane vechi, deci tipul iese la fel cu
 * oricare altul si se poate duce mai departe de acolo.
 *
 * Ecranul apare NUMAI pentru un tip gol. Unul care are deja flux se schimba din
 * fisa lui, pas cu pas, unde se si vede ce era acolo inainte.
 */
const KINDS: Record<number, string> = {
  1: 'while it is being worked on',
  2: 'resolved',
  3: 'the ticket ends here',
  4: 'approved',
  5: 'denied',
}

/*
 * Ce se propune la deschidere.
 *
 * Trei faze, fiindca astea sunt cele fara de care un tichet nu poate trai: una
 * de intrare, una de lucru, si una pe care se INCHIDE. Ultima e de felul
 * "final" - numai pe un status final se poate inchide un tichet, si e greseala
 * pe care o face oricine se gandeste ca "Resolved" ar fi de ajuns.
 */
const START: BuildPhase[] = [
  { name: 'New', kind: 1, color: '#C0392B', fields: [] },
  { name: 'In progress', kind: 1, color: '#16A085', fields: [] },
  { name: 'Closed', kind: 3, color: '#7F8C8D', fields: [] },
]

function PhasePanel({
  phase,
  at,
  total,
  pieces,
  known,
  onChange,
  onDrop,
}: {
  phase: BuildPhase
  at: number
  total: number
  pieces: BuildPiece[]
  known: Record<string, number>
  onChange(next: BuildPhase): void
  onDrop(): void
}) {
  /*
   * Un nume care exista deja aduce STATUSUL acela, cu felul lui: statusurile
   * sunt randuri comune tuturor instantelor. Se spune aici, cat inca se poate
   * schimba numele, nu dupa ce s-a scris.
   */
  const taken = known[phase.name.trim().toLowerCase()]
  const clash = taken !== undefined && taken !== phase.kind

  const ticked = (piece: BuildPiece) =>
    phase.fields.some((one) => one.id === piece.id && one.custom === (piece.custom === 1))

  const toggle = (piece: BuildPiece, on: boolean) => {
    const custom = piece.custom === 1
    onChange({
      ...phase,
      fields: on
        ? [...phase.fields, { id: piece.id, custom }]
        : phase.fields.filter((one) => !(one.id === piece.id && one.custom === custom)),
    })
  }

  return (
    <div className="panel panel-default build-phase" style={{ marginBottom: 10 }}>
      <div
        className="panel-heading"
        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
      >
        <span className="color-light-grey" style={{ fontFamily: 'monospace' }}>
          {at + 1}
        </span>
        <input
          type="text"
          className="form-control input-sm build-name"
          value={phase.name}
          placeholder="Name of the phase"
          style={{ width: 200 }}
          onChange={(e) => onChange({ ...phase, name: e.target.value })}
        />
        <select
          className="form-control input-sm build-kind"
          style={{ width: 210 }}
          value={phase.kind}
          onChange={(e) => onChange({ ...phase, kind: Number(e.target.value) })}
        >
          {Object.entries(KINDS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="color"
          className="form-control input-sm build-color"
          value={phase.color}
          title="Colour"
          style={{ width: 46, padding: 2 }}
          onChange={(e) => onChange({ ...phase, color: e.target.value })}
        />
        {total > 2 ? (
          <a
            href="javascript:void(0)"
            className="build-drop color-danger"
            style={{ marginLeft: 'auto', fontSize: 12 }}
            onClick={onDrop}
          >
            <i className="fas fa-times" aria-hidden="true"></i> drop
          </a>
        ) : null}
      </div>

      {clash ? (
        <div className="panel-body" style={{ padding: '8px 14px 0' }}>
          <p className="color-warning" style={{ fontSize: 11.5, margin: 0 }}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> A status called &ldquo;{phase.name}
            &rdquo; already exists and is <b>{KINDS[taken]}</b>. It will be used as it is &mdash; statuses are shared
            by every instance. Give the phase another name if you need it to be {KINDS[phase.kind]}.
          </p>
        </div>
      ) : null}

      <div className="panel-body" style={{ padding: '10px 14px' }}>
        <div className="color-light-grey" style={{ fontSize: 11, marginBottom: 4 }}>
          what is asked for here
        </div>
        {pieces.map((piece) => (
          <label
            key={`${piece.custom}-${piece.id}`}
            style={{ display: 'inline-block', margin: '0 10px 4px 0', fontWeight: 'normal', fontSize: 12.5 }}
          >
            <input
              type="checkbox"
              className="build-piece"
              checked={ticked(piece)}
              onChange={(e) => toggle(piece, e.target.checked)}
            />{' '}
            {piece.name}
            <span className="color-light-grey" style={{ fontSize: 11 }}>
              {' '}
              {piece.type}
              {piece.custom === 1 ? ' · yours' : ''}
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}

export function TicketTypeBuildPage() {
  const { type } = useParams({ strict: false }) as { type?: string }
  const typeId = Number(type ?? 0)
  const navigate = useNavigate()

  const page = useQuery(buildTypeFormQuery(typeId))
  const [phases, setPhases] = useState<BuildPhase[]>(START)

  useEffect(() => {
    setPhases(START.map((phase) => ({ ...phase, fields: [] })))
  }, [typeId])

  const go = useMutation({
    mutationFn: () => buildTicketType(typeId, phases),
    onSuccess: (answer) => {
      const built = answer.built
      toast(
        `${built?.statuses.length ?? 0} phases, ${built?.forms ?? 0} forms, ${built?.fields ?? 0} pieces written.`,
        'Ticket type built',
      )
      if (answer.closable === false) {
        toast(
          'No phase of this type ends the ticket, so it cannot be closed. Add a final status from the sheet.',
          'Ticket type',
          'error',
        )
      }
      /* Direct in fisa lui, pe fila fluxului: de acolo incolo se schimba pas cu pas. */
      void navigate({ to: '/settings/easeedesk/ticket_type_overview/$type/$pane', params: { type: String(typeId), pane: 'flow' } })
    },
    onError: (error) =>
      toast(
        error instanceof ApiError && error.message ? error.message : 'The type could not be built.',
        'Ticket type',
        'error',
      ),
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error instanceof ApiError && page.error.status === 404) {
    return <div className="alert alert-danger">This ticket type does not exist.</div>
  }
  if (!page.data) return <Spinner />

  const data = page.data

  if (!data.buildable) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> This type already has a flow. Change it from its
        sheet, step by step.
      </div>
    )
  }

  const change = (at: number, next: BuildPhase) =>
    setPhases((current) => current.map((phase, i) => (i === at ? next : phase)))

  const drop = (at: number) => setPhases((current) => current.filter((_, i) => i !== at))

  const add = () =>
    setPhases((current) => {
      /* Faza noua intra INAINTEA ultimei: ultima e cea pe care se inchide
         tichetul, si aia ramane la sfarsit. */
      const next = [...current]
      next.splice(next.length - 1, 0, { name: '', kind: 1, color: '#7F8C8D', fields: [] })
      return next
    })

  return (
    <div>
      <div className="row">
        <div className="col-md-12">
          <h4 style={{ marginTop: 0 }}>
            <span
              className="badge"
              style={{ backgroundColor: data.type.color_identifier, color: '#fff', textAlign: 'center' }}
            >
              {data.type.short_name}
            </span>{' '}
            {data.type.type_name}
          </h4>

          <p className="color-light-grey" style={{ marginBottom: 18 }}>
            Nothing is set up for this type yet. Write the phases a ticket goes through and what is asked for on each;
            the flow, the statuses and the forms are written for you. Anything you leave out can be changed afterwards
            from the type&apos;s sheet.
          </p>
        </div>
      </div>

      <div id="build-phases">
        {phases.map((phase, at) => (
          <PhasePanel
            key={at}
            phase={phase}
            at={at}
            total={phases.length}
            pieces={data.pieces}
            known={data.known}
            onChange={(next) => change(at, next)}
            onDrop={() => drop(at)}
          />
        ))}
      </div>

      <div style={{ margin: '14px 0 20px' }}>
        <button type="button" className="btn btn-default btn-sm" id="build-add-phase" onClick={add}>
          <i className="fas fa-plus" aria-hidden="true"></i> another phase
        </button>

        <button
          type="button"
          className="btn btn-primary btn-sm pull-right"
          id="build-go"
          disabled={go.isPending}
          onClick={() => {
            if (phases.some((phase) => phase.name.trim() === '')) {
              toast('Every phase needs a name.', 'Ticket type', 'error')
              return
            }
            go.mutate()
          }}
        >
          <i className="fas fa-check" aria-hidden="true"></i> Build this type
        </button>
      </div>
    </div>
  )
}
